import b from 'b_';
import { useIntl } from 'platform-components';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { QuizEditorAnswerCheckbox } from '../__answer-checkbox/quiz-editor__answer-checkbox';
import { QuizEditorInput } from '../__input/quiz-editor__input';
import { actions } from '../actions';
import { getAnswerMaxLength } from '../selectors';

import './quiz-editor__answer.scss';

export type AnswerCardColors = 'blue' | 'aqua' | 'red' | 'yellow';

interface Props {
    color: AnswerCardColors;
    answerIndex: number;
    onCheckboxClick: (answerIndex: number) => void;
    value: string;
    isCorrect: boolean;
    onAnswerInputBlur: (answerIndex: number, value: string) => void;
    questionIndex: number;
    readOnly?: boolean;
}

const INPUT_MAX_HEIGHT = 112;

export const QuizEditorAnswer = (props: Props) => {
    const { color, answerIndex, onCheckboxClick, value, isCorrect, onAnswerInputBlur, questionIndex, readOnly } = props;

    const dispatch = useDispatch();

    const answerMaxLength = useSelector(getAnswerMaxLength);

    const intl = useIntl();
    const answerPlaceholder = intl.formatMessage({ id: 'teacherLab.quizEditor.answerPlaceholder' });

    const [answerValue, setAnswerValue] = useState(value);
    const answerInputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        setAnswerValue(value);
    }, [value]);

    const deferredSavingTimer = useRef(0);

    useEffect(() => {
        deferredSavingTimer.current = window.setTimeout(() => {
            dispatch(
                actions.setAnswerValue({
                    answerIndex,
                    questionIndex,
                    value: answerValue,
                })
            );
        }, 400);

        return () => {
            window.clearTimeout(deferredSavingTimer.current);
        };
    }, [answerIndex, answerValue, dispatch, questionIndex]);

    const handleCheckboxClick = () => {
        onCheckboxClick(answerIndex);
    };

    const handleInputBlur = () => {
        onAnswerInputBlur(answerIndex, answerValue);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setAnswerValue(event.target.value);
    };

    const handleAnswerClick = () => {
        if (answerInputRef.current) {
            answerInputRef.current.focus();
        }
    };

    return (
        <div
            className={b('quiz-editor__answer', { correct: readOnly && isCorrect })}
            {...accessibleClick(handleAnswerClick)}
        >
            <div className={b('answer__content', { color })}>
                {!readOnly && <QuizEditorAnswerCheckbox checked={isCorrect} onClick={handleCheckboxClick} />}
                <QuizEditorInput
                    counterClassName="answer__symbols-counter"
                    iconClassName="answer__edit-icon"
                    inputRef={answerInputRef}
                    maxHeight={INPUT_MAX_HEIGHT}
                    maxLength={answerMaxLength}
                    onBlur={handleInputBlur}
                    onChange={handleInputChange}
                    placeholder={answerPlaceholder}
                    readOnly={readOnly}
                    textareaClassname={b('answer__input', { 'read-only': readOnly })}
                    value={answerValue}
                />
            </div>
        </div>
    );
};
